import SeatPlanManage from "components/billing/SeatPlanManage";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import DropDown from "components/DesignSystem/Dropdown/Dropdown";
import { OptionDropdown } from "components/icons/optionDropdown";
import { INVITED } from "constants/groupRequest";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useState } from "react";
import { AddOrEditTeamMemberModal } from "./AddEditTeamMemberModal";
import ArchiveMemberModal from "./ArchiveMemberModal";
import { ManageProfile } from "./ManageProfile";
import { ResendInvite } from "./ResendInvite";
import { RevokeInvite } from "./RevokeInvite";
import { InviteUpdateUserModal } from "./InviteUpdateUserModal";

export const TeamMemberOptions = ({
  profileId,
  isArchived,
  updateArchiveToast,
  isSuperAdmin,
  ownAccount,
  multipleOwnerOfGroupExist,
  user,
  availableSeats,
  options,
  groupName,
  groupId,
  openInviteUserSeatManageModal,
  seatData,
}: {
  profileId: string;
  isArchived: boolean;
  updateArchiveToast: (v: string) => void;
  isSuperAdmin: boolean;
  ownAccount: boolean;
  multipleOwnerOfGroupExist: boolean;
  user: {
    uuid: string;
    email: string;
    status: string;
    profile: string;
    role_name: string;
    role_uuid: string;
    group_role_id: string;
    allowed_access: boolean;
    name: string;
    first_name: string;
    last_name: string;
    mobile: string;
    linkedIn_url: string;
    current_timezone: string;
    secondary_mobile: string;
    profile_id: string;
    available_secondary_whatsapp: boolean;
    available_primary_whatsapp: boolean;
    calendar_url: string;
    whatsapp_consent: boolean;
  };
  options: { name: string; uuid: string }[];
  groupName: string;
  groupId: string;
  availableSeats: number;
  openInviteUserSeatManageModal?: () => void;
  seatData: {
    active_group_users_count: number;
    available_seats: number;
    extra_seat_amount: number;
    free_seats: number;
  };
}) => {
  const { isAdmin, isCustomer } = useRoleBasedView();
  const [show, setShow] = useState(false);
  const superAdminTooltip = " Primary owner cannot be archived";
  const {
    open: openUserEditModal,
    close: closeUserEditModal,
    isOpen: showUserEditModal,
  } = useModal();
  const {
    open: openSeatManageModal,
    close: closeSeatManageModal,
    isOpen: showSeatManageModal,
  } = useModal();
  const {
    open: openRevokeInviteModal,
    close: closeRevokeInviteModal,
    isOpen: showRevokeInviteModal,
  } = useModal();
  const {
    open: openResendInviteModal,
    close: closeResendInviteModal,
    isOpen: showResendInviteModal,
  } = useModal();
  const deleteModal = useModal();
  const manageProfileModal = useModal();
  const isInvitedUser = user.status === INVITED;

  const closeSeatManage = () => {
    closeSeatManageModal();
    openUserEditModal();
  };

  return (
    <>
      <DropDown.Root modal={false}>
        <DropDown.Trigger className="all:unset">
          <Button customType="ghost_icon" size="xsmall">
            <OptionDropdown color="currentColor" />
          </Button>
        </DropDown.Trigger>
        <DropDown.Portal>
          <DropDown.Content
            sideOffset={8}
            className="t-rounded-lg t-bg-surface t-p-2 t-drop-shadow-dropdown sm:t-w-[94vw]"
            align="end"
          >
            {isInvitedUser && !isCustomer && (
              <>
                <DropDown.Item
                  className="disable:t-text-gray t-flex t-items-center t-gap-2 t-rounded hover:t-bg-i-surface-light-purple"
                  onSelect={openRevokeInviteModal}
                >
                  <span>Revoke invite</span>
                </DropDown.Item>
                <DropDown.Item
                  className="disable:t-text-gray t-flex t-items-center t-gap-2 t-rounded hover:t-bg-i-surface-light-purple"
                  onSelect={openResendInviteModal}
                >
                  <span>Resend invite</span>
                </DropDown.Item>
              </>
            )}
            {(!isInvitedUser || isCustomer) && (
              <>
                {isAdmin && !isInvitedUser && (
                  <DropDown.Item
                    className="disable:t-text-gray t-flex t-items-center t-gap-2 t-rounded hover:t-bg-i-surface-light-purple"
                    onSelect={manageProfileModal.open}
                  >
                    <span>Manage profile</span>
                  </DropDown.Item>
                )}
                {isArchived ? (
                  <DropDown.Item
                    className="disable:t-text-gray t-flex t-items-center t-gap-2 t-rounded hover:t-bg-i-surface-light-purple"
                    onSelect={() => setShow(true)}
                  >
                    <span>Unarchive user</span>
                  </DropDown.Item>
                ) : (
                  <>
                    <DropDown.Item
                      className="disable:t-text-gray t-flex t-items-center t-gap-2 t-rounded hover:t-bg-i-surface-light-purple"
                      onSelect={openUserEditModal}
                    >
                      <span>Update user</span>
                    </DropDown.Item>
                    {!ownAccount && (
                      <DropDown.Item
                        disabled={isSuperAdmin && !multipleOwnerOfGroupExist}
                        className="disable:t-text-gray t-flex t-items-center t-gap-2 t-rounded hover:t-bg-i-surface-light-purple"
                        onSelect={() => setShow(true)}
                      >
                        <ConditionalToolTip
                          condition={
                            isSuperAdmin &&
                            !multipleOwnerOfGroupExist &&
                            superAdminTooltip
                          }
                        >
                          <span>Archive user</span>
                        </ConditionalToolTip>
                      </DropDown.Item>
                    )}
                  </>
                )}
              </>
            )}
          </DropDown.Content>
        </DropDown.Portal>
      </DropDown.Root>
      {show && (
        <ArchiveMemberModal
          profileId={profileId}
          show={show}
          closeModal={() => setShow(false)}
          isArchived={isArchived}
          updateArchiveToast={updateArchiveToast}
        />
      )}
      {showUserEditModal && isCustomer && (
        <InviteUpdateUserModal
          open={showUserEditModal}
          onClose={closeUserEditModal}
          user={{
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            profile_id: user.profile_id,
            role_id: user.group_role_id,
            uuid: user.uuid,
            allowed_access: user.allowed_access,
          }}
        />
      )}
      {showUserEditModal && !isCustomer && (
        <AddOrEditTeamMemberModal
          show={showUserEditModal}
          closeModal={() => closeUserEditModal()}
          options={options}
          groupName={groupName}
          groupId={groupId}
          availableSeats={availableSeats}
          user={user}
          openInviteUserSeatManageModal={openInviteUserSeatManageModal}
          openSeatManageModal={openSeatManageModal}
          manageUserFlow
        />
      )}
      <RevokeInvite
        show={showRevokeInviteModal}
        closeModal={closeRevokeInviteModal}
        groupId={groupId}
        email={user.email}
      />
      <RevokeInvite
        show={deleteModal.isOpen}
        closeModal={deleteModal.close}
        groupId={groupId}
        email={user.email}
        title="Delete user"
        body="Are you sure you want to delete this user? This action cannot be undone and the user will lose access to the platform."
        confirmLabel="Delete"
      />
      <ResendInvite
        show={showResendInviteModal}
        closeModal={closeResendInviteModal}
        groupId={groupId}
        profileId={user.profile_id}
      />
      <SeatPlanManage
        show={showSeatManageModal}
        closeModal={closeSeatManage}
        seatData={seatData}
        handlePlanManageSuccess={closeSeatManage}
      />
      <ManageProfile
        show={manageProfileModal.isOpen}
        closeModal={manageProfileModal.close}
        user={user}
      />
    </>
  );
};
